import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
    Toast,
    Button,
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionGroup,
    StandardModalDivider,
    StandardModalFormGroup,
    StandardModalHelperText,
    StandardModalLabel,
    StandardModalSection,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection
} from "src/components";
import { portalColors } from "src/components/BusinessPortalComponents";
import {
    createUserListDocumentType,
    deleteUserListDocumentType,
    updateUserListDocumentType,
} from "src/components/Documents/api/documentsApi";
import Checkbox from "./Checkbox";

const Body = styled.div`
    display: grid;
    gap: 1rem;
    min-height: 0;
`;

const Row = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;

    > input {
        flex: 1 1 18rem;
    }
`;

const FooterSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    flex-wrap: wrap;
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const LeftButtons = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
    width: ${p => p.width || "172px"};
    height: ${p => p.height || "39px"};
    border-radius: ${p => p.borderRadius || "7px"} !important;
    box-shadow: 0px 3px 6px #00000029;
    opacity: 1;
    display: flex;
    justify-content: center;
    align-items: center;

    background: ${p => p.background || portalColors.surface};
    color: ${p => p.color || portalColors.text};
    border: ${p => p.border || `1px solid ${portalColors.borderStrong}`};
`;

const CheckboxCell = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const AssignedUsers = styled.span`
    font-weight: 700;
`;

export const StyledInput = styled.input`
    color: ${portalColors.text};
    padding: 0 1rem;
    margin: 0;
    height: 2.5rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    text-align: left;
    width: 475px;
    max-width: 100%;
    box-sizing: border-box;
    background: ${portalColors.surfaceSoft};
    box-shadow: 0px 3px 6px #00000029;
    border: 1px solid ${portalColors.border};
    border-radius: 12px;
    opacity: 1;

    ::placeholder {
        text-align: left;
    }
`;

const ModalContainer = styled.div`
    position: relative;
    min-width: 0;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: color-mix(in srgb, var(--calendar-surface, #FFFFFF) 78%, transparent);
    z-index: 999;
`;

const SelectableTable = styled(StandardModalTable)`
    tr.selected td {
        background: var(--calendar-selected-cell-bg, #eaf3ff);
    }

    tr.selection-disabled {
        cursor: not-allowed;
    }

    tr.selection-disabled:not(.selected) td {
        opacity: 0.45;
    }
`;

export function ManageDocumentTypesModal({ open, onClose, documentTypes, refresh }) {
    const inputRef = useRef(null);
    const [description, setDescription] = useState("");
    const [selectedDocumentUuids, setSelectedDocumentUuids] = useState([]);
    const [editingDocumentUuid, setEditingDocumentUuid] = useState(null);
    const [saving, setSaving] = useState(false);

    const selectedDocument = selectedDocumentUuids.length === 1
        ? documentTypes.find(d => d.uuid === selectedDocumentUuids[0])
        : null;
    const hasSelectedDocuments = selectedDocumentUuids.length > 0;
    const canEditDocument = selectedDocumentUuids.length === 1;
    const isEditingDocument = Boolean(editingDocumentUuid);

    const toggleSelectedDocument = (documentUuid) => {
        setSelectedDocumentUuids(prev => {
            const isEditing = Boolean(editingDocumentUuid);

            if (isEditing) {
                if (editingDocumentUuid !== documentUuid) {
                    return prev;
                }

                return prev.includes(documentUuid)
                    ? []
                    : [documentUuid];
            }

            const isSelected = prev.includes(documentUuid);

            return isSelected
                ? prev.filter(uuid => uuid !== documentUuid)
                : [...prev, documentUuid];
        });
    };

    const canSaveDocumentType =
        description.trim().length > 0 &&
        selectedDocumentUuids.length <= 1;

    const startEditingDocument = () => {
        if (!selectedDocument) return;

        setEditingDocumentUuid(selectedDocument.uuid);
        setSelectedDocumentUuids([selectedDocument.uuid]);
        setDescription(selectedDocument.description);
    };

    const resetEditingState = () => {
        setEditingDocumentUuid(null);
        setSelectedDocumentUuids([]);
        setDescription("");
    };

    const handleSaveDocumentType = async () => {
        if (selectedDocumentUuids.length > 1) return Toast.error("Select only one document type to edit.");
        if (!description.trim()) return Toast.error("Enter document type name.");

        setSaving(true);
        try {
            if (selectedDocument) {
                await updateUserListDocumentType(selectedDocument.uuid, description.trim());
            } else {
                await createUserListDocumentType(description.trim());
            }
            resetEditingState();
            await refresh();
        } catch (e) {
            Toast.error(e.getApiErrorDetails().message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!hasSelectedDocuments) return Toast.error("Select a document type.");
        setSaving(true);
        try {
            await Promise.all(selectedDocumentUuids.map(documentUuid => deleteUserListDocumentType(documentUuid)));
            resetEditingState();
            await refresh();
        } catch {
            Toast.error(
                selectedDocumentUuids.length > 1
                    ? "Unable to delete selected document types."
                    : "Unable to delete document type."
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (!selectedDocument) return onClose();
        if (description.trim() === "" || description === selectedDocument.description) return onClose();

        setSaving(true);
        try {
            await updateUserListDocumentType(selectedDocument.uuid, description.trim());
            await refresh();
            resetEditingState();
            onClose();
        } catch (e) {
            Toast.error(e.getApiErrorDetails().message);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (editingDocumentUuid && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingDocumentUuid]);
    
    useEffect(() => {
        if (selectedDocumentUuids.length === 0) {
            setEditingDocumentUuid(null);
            setDescription("");
        }
    }, [selectedDocumentUuids]);

    const emptyRows = Math.max(0, 10 - documentTypes.length);

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            widthMode="compactTable"
            title="Invite Documents"
            description="Manage and organise the document types available for user invites. Create custom document requests, assign them to users, and control which documents are required during onboarding or account access setup."
            footer={(
                <FooterSection>
                    <ButtonRow>
                        <LeftButtons>
                            {hasSelectedDocuments && (
                                <>
                                    {canEditDocument && !isEditingDocument && (
                                        <ActionButton
                                            background="#fff"
                                            color="#000"
                                            border="1px solid #000"
                                            width="172px"
                                            onClick={startEditingDocument}
                                        >
                                        Edit Document Name
                                        </ActionButton>
                                    )}

                                    <ActionButton
                                        background="#FFF5F5"
                                        color="#FF3B3B"
                                        border="1px solid #FF3B3B"
                                        width="172px"
                                        onClick={handleDelete}
                                    >
                                        {selectedDocumentUuids.length > 1 ? "Delete Documents" : "Delete Document"}
                                    </ActionButton>
                                </>
                            )}
                        </LeftButtons>
                    </ButtonRow>
                    <StandardModalActionGroup $pushRight>
                        <ActionButton
                            background="#fff"
                            color="#000"
                            border="1px solid #000"
                            width="140px"
                            height="38px"
                            onClick={onClose}
                        >
                        Cancel
                        </ActionButton>

                        <ActionButton
                            background="#171A22"
                            color="#fff"
                            border="none"
                            width="140px"
                            height="38px"
                            onClick={handleSave}
                        >
                        Save
                        </ActionButton>
                    </StandardModalActionGroup>
                </FooterSection>
            )}
        >
            <ModalContainer>
                {saving && (
                    <LoadingOverlay>
                        <LottieLoadingIndicator />
                    </LoadingOverlay>
                )}
                <Body>
                    <StandardModalSection>
                        <StandardModalFormGroup>
                            <StandardModalLabel>Create Document Type</StandardModalLabel>
                            <StandardModalHelperText>Enter a name for this document type. Example: Passport.</StandardModalHelperText>
                        </StandardModalFormGroup>
                        <Row>
                            <StyledInput
                                ref={inputRef}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Enter document type name"
                            />

                            <ActionButton
                                background={canSaveDocumentType ? "#000" : "#D9D9D9"}
                                color={canSaveDocumentType ? "#FFF" : "#888"}
                                border="none"
                                width="172px"
                                disabled={!canSaveDocumentType}
                                onClick={handleSaveDocumentType}
                                style={{
                                    cursor: canSaveDocumentType ? "pointer" : "not-allowed"
                                }}
                            >
                            Save Document Type
                            </ActionButton>
                        </Row>
                    </StandardModalSection>
                    <StandardModalDivider />
                    <StandardModalTableSection>
                        <StandardModalTableScroll maxHeight="22rem">
                            <SelectableTable>
                                <thead>
                                    <tr>
                                        <th align="left">{documentTypes.length} Documents Types Created</th>
                                        <th align="left">Users Assigned</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {documentTypes.map(doc => (
                                        <tr
                                            key={doc.uuid}
                                            className={`${selectedDocumentUuids.includes(doc.uuid) ? "selected" : ""} ${isEditingDocument ? "selection-disabled" : ""}`}
                                            onClick={() => {
                                                if (isEditingDocument && editingDocumentUuid !== doc.uuid) return;
                                                toggleSelectedDocument(doc.uuid);
                                            }}
                                        >
                                            <td>
                                                <CheckboxCell style={{
                                                    fontFamily: "Manrope, sans-serif",
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    lineHeight: "22px"
                                                }}>
                                                    <Checkbox
                                                        type="checkbox"
                                                        checked={selectedDocumentUuids.includes(doc.uuid)}
                                                        disabled={isEditingDocument}
                                                        readOnly
                                                    />
                                                    {doc.description}
                                                </CheckboxCell>
                                            </td>

                                            <td>
                                                <AssignedUsers style={{
                                                    fontFamily: "Manrope, sans-serif",
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    lineHeight: "22px"
                                                }}>
                                                    {(doc.assignedUsers || []).length} Users Assigned
                                                </AssignedUsers>
                                            </td>
                                        </tr>
                                    ))}

                                    {Array.from({ length: emptyRows }).map((_, i) => (
                                        <tr key={`empty-${i}`}>
                                            <td>
                                                <CheckboxCell>
                                                    <Checkbox type="checkbox" disabled />
                                                    <span style={{ opacity: 0 }}>placeholder</span>
                                                </CheckboxCell>
                                            </td>
                                            <td>
                                                <span style={{ opacity: 0 }}>0 Users Assigned</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </SelectableTable>
                        </StandardModalTableScroll>
                    </StandardModalTableSection>
                </Body>
            </ModalContainer>
        </StandardModal>
    );
}
